import { UseFormRegister, UseFormWatch, FieldErrors } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import TagInput from "./TagInput";
import EventBasicInfo from "./EventBasicInfo";
import EventDateTime from "./EventDateTime";
import EventCapacityPrice from "./EventCapacityPrice";
import HostSelect from "./HostSelect";
import CoHostSelect from "./CoHostSelect";
import LocationPicker from "./LocationPicker";
import ImageUpload from "./ImageUpload";
import AgendaInput from "./AgendaInput";
import RichTextArea from "./RichTextArea";
import EventLocations from "./EventLocations";
import BookingMethodsSelect from "./BookingMethodsSelect";
import RequiredTagsSelect from "./RequiredTagsSelect";
import EventGroupsSection from "./EventGroupsSection";
import { EventFormData } from "./types";

interface EventFormFieldsProps {
  register: UseFormRegister<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  errors: FieldErrors<EventFormData>;
  setValue: (name: keyof EventFormData, value: any) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  onLocationSelect: (venue: string, lat: number, lng: number) => void;
  defaultVenue?: string;
  defaultHost?: string;
  hostType: string;
  setHostType: (hostType: string) => void;
  eventId?: string;
  onGroupsValidityChange: (error: string | null) => void;
}

const EventFormFields = ({
  register,
  watch,
  errors,
  setValue,
  tags,
  onTagsChange,
  onLocationSelect,
  defaultVenue,
  defaultHost,
  hostType,
  setHostType,
  eventId,
  onGroupsValidityChange,
}: EventFormFieldsProps) => {
  const hostId = watch("hostId");
  const secondaryHostId = watch("secondaryHostId");
  const description = watch("description") || "";
  const agendaText = watch("agendaText") || "";
  const allowedBookingMethods = watch("allowedBookingMethods") || ["stripe", "credits", "unlimited"];
  const requiredTags = watch("requiredTags") || [];

  // Watch fields needed for AI generation
  const title = watch("title") || "";
  const venue = watch("venue") || "";
  const startTimeValue = watch("startTime") || "";
  const endTimeValue = watch("endTime") || "";
  const price = watch("price");
  const creditCost = watch("creditCost");

  // Extract date from startTime (ISO format: YYYY-MM-DDTHH:MM)
  const startDate = startTimeValue ? startTimeValue.split("T")[0] : "";
  const startTimeOnly = startTimeValue ? startTimeValue.split("T")[1] || "" : "";
  const endTimeOnly = endTimeValue ? endTimeValue.split("T")[1] || "" : "";

  return (
    <div className="space-y-6">
      <EventBasicInfo register={register} errors={errors} />
      <LocationPicker
        onLocationSelect={onLocationSelect}
        defaultVenue={defaultVenue}
      />
      <EventLocations
        eventId={eventId}
        onChange={(locations) => setValue("locations", locations)}
      />
      <EventDateTime register={register} errors={errors} />
      <EventCapacityPrice register={register} errors={errors} watch={watch} setValue={setValue} />
      <EventGroupsSection
        eventId={eventId}
        groups={watch("groups") || []}
        eventCapacity={parseInt(String(watch("capacity") ?? 0), 10) || 0}
        eventStartTime={startTimeValue}
        eventHostId={hostId}
        eventSecondaryHostId={secondaryHostId}
        onChange={(groups) => setValue("groups", groups)}
        onValidityChange={onGroupsValidityChange}
      />
      <div className="space-y-2">
        <Label>Event Image</Label>
        <ImageUpload
          url={watch("imageUrl")}
          onUpload={(url) => setValue("imageUrl", url)}
        />
      </div>

      <RichTextArea
        id="description"
        value={description}
        onChange={(value) => setValue("description", value)}
        label="Description"
        placeholder="Enter event description..."
        helpText="Formatting and case sensitivity will be preserved. You can use HTML tags for:
          - Bold text: <b>bold</b> or <strong>bold</strong>
          - Links: <a href='url'>link text</a>"
        aiEnabled={true}
        eventContext={{
          title,
          venue,
          date: startDate,
          startTime: startTimeOnly,
          endTime: endTimeOnly,
          price: Number(price) || 0,
          creditCost: creditCost ? Number(creditCost) : undefined,
          tags,
        }}
      />

      <div className="space-y-2">
        <Label>Agenda</Label>
        <AgendaInput
          value={agendaText}
          onChange={(value) => setValue("agendaText", value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsappGroup">WhatsApp Group Link</Label>
        <Input id="whatsappGroup" {...register("whatsappGroup")} />
      </div>

      <HostSelect
        value={hostId}
        hostType={hostType}
        onHostTypeChange={setHostType}
        onChange={(hostId) => setValue("hostId", hostId)}
      />

      <CoHostSelect
        value={secondaryHostId}
        primaryHostId={hostId}
        onChange={(id) => setValue("secondaryHostId", id)}
      />

      <div className="space-y-2">
        <Label>Tags</Label>
        <TagInput tags={tags} onTagsChange={onTagsChange} />
      </div>

      <BookingMethodsSelect
        value={allowedBookingMethods}
        onChange={(methods) => setValue("allowedBookingMethods", methods)}
      />

      <RequiredTagsSelect
        value={requiredTags}
        onChange={(tags) => setValue("requiredTags", tags)}
      />
    </div>
  );
};

export default EventFormFields;
